import { useCallback, useEffect, useRef } from 'react';

export interface InfiniteScrollSentinelParams {
  hasNextPage: boolean;
  isFetching: boolean;
  fetchNextPage: () => void;
  rootMargin?: string;
}

/**
 * Wires an IntersectionObserver to a sentinel DOM node that, when scrolled
 * into view, drives `fetchNextPage` for a `useInfiniteQuery`-backed hook.
 *
 * Returns a callback ref to attach to a DOM element rendered at the end of
 * the scrollable list. The sentinel can be conditionally rendered — the
 * observer attaches and detaches as the node mounts/unmounts.
 *
 * Pure DOM-side glue; lives in `src/hooks/` rather than inside any resource
 * folder because every IntersectionObserver-based infinite scroller in the
 * app shares the exact same wiring.
 */
export function useInfiniteScrollSentinel({
  hasNextPage,
  isFetching,
  fetchNextPage,
  rootMargin = '200px',
}: InfiniteScrollSentinelParams): (node: Element | null) => void {
  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;
  const enabledRef = useRef(hasNextPage && !isFetching);
  enabledRef.current = hasNextPage && !isFetching;

  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelRef = useCallback(
    (node: Element | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && enabledRef.current) {
              fetchNextPageRef.current();
            }
          }
        },
        { rootMargin }
      );
      observerRef.current.observe(node);
    },
    [rootMargin]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return sentinelRef;
}
